// The skin editor's state and reducer. Pure — no React, no DOM — so the self-test drives the exact
// reducer the UI uses (undo/redo of layer operations, stroke grouping, restore-vs-explicit-load).

import type {
  Tool,
  Color,
  ModelType,
  Layer,
  LayerViewMode,
  BodyPart,
  BodyPartVisibility,
  BrushSize,
  Point,
  ShapePreview,
} from './types';
import {
  type SkinDocument,
  type SkinLayer,
  createBlankDocument,
  documentFromImportedSkin,
  isBlankDocument,
  addLayer as addLayerOp,
  deleteLayer as deleteLayerOp,
  duplicateLayer as duplicateLayerOp,
  moveLayer as moveLayerOp,
  updateLayerProps,
  mergeDown as mergeDownOp,
  flattenDocument,
  setActiveLayer as setActiveLayerOp,
  SKIN_BYTES,
} from './layers';
import { type History, emptyHistory, recordHistory, undoHistory, redoHistory } from './history';
import { paintStroke, paintShape, paintFill, type PaintSettings } from './edits';
import type { Bounds } from './paint';
import type { AutosaveState } from './autosave';

// Initial visibility state - all parts visible
const initialVisibility: Record<BodyPart, BodyPartVisibility> = {
  head: { base: true, overlay: true },
  body: { base: true, overlay: true },
  rightArm: { base: true, overlay: true },
  leftArm: { base: true, overlay: true },
  rightLeg: { base: true, overlay: true },
  leftLeg: { base: true, overlay: true },
};

function allVisibility(base: boolean, overlay: boolean): Record<BodyPart, BodyPartVisibility> {
  return {
    head: { base, overlay }, body: { base, overlay }, rightArm: { base, overlay },
    leftArm: { base, overlay }, rightLeg: { base, overlay }, leftLeg: { base, overlay },
  };
}

/**
 * One undo entry: the document AND the arm model. The model is part of what a skin load, "Start new"
 * or a Classic/Slim switch changes, so undoing one of those restores it too (undoing a load used to
 * leave the loaded skin's model behind).
 */
export interface EditorSnapshot {
  doc: SkinDocument;
  modelType: ModelType;
}

export interface EditorState {
  /** The layered document. Immutable — see app/lib/skinEditor/layers.ts. */
  doc: SkinDocument;
  history: History<EditorSnapshot>;
  /** Document at the start of the open stroke (null when no stroke is open). */
  strokeBase: SkinDocument | null;
  /** Whether the open stroke has already recorded its undo entry. */
  strokeRecorded: boolean;
  /** The undo history as it was when the open stroke began — what CANCEL_STROKE puts back. */
  strokeHistory: History<EditorSnapshot> | null;
  /** Consecutive edits with the same key share one undo entry (an opacity-slider drag). */
  lastCoalesceKey: string | null;
  tool: Tool;
  color: Color;
  secondaryColor: Color;
  brushSize: BrushSize;
  /** Lighten / Darken / Noise strength in percent (SHADE_STRENGTH_MIN..MAX) of OKLab lightness. */
  shadeStrength: number;
  /** Seed of the current/last stroke — bumped by every BEGIN_STROKE (the Noise brush). */
  strokeSeed: number;
  modelType: ModelType;
  visibleParts: Record<BodyPart, BodyPartVisibility>;
  mirrorMode: boolean;
  isLoading: boolean;
  viewerMode: 'rotate' | 'paint';
  showGrid: boolean;
  shapePreview: ShapePreview | null;
  hoverCoords: { x: number; y: number } | null;
  layerViewMode: LayerViewMode;
  /** The user changed something since the page opened (or since the last explicit load) → autosave. */
  dirty: boolean;
  /** An explicit load (?skinUrl=, Saved-page handoff, username/URL/upload) happened or is pending. */
  explicitLoad: boolean;
  /** The document came from the autosave — shows the "Restored your last session" notice. */
  restored: boolean;
}

export const initialState: EditorState = {
  doc: createBlankDocument(),
  history: emptyHistory(),
  strokeBase: null,
  strokeRecorded: false,
  strokeHistory: null,
  lastCoalesceKey: null,
  tool: 'pencil',
  color: { r: 0, g: 0, b: 0, a: 255 },
  secondaryColor: { r: 200, g: 200, b: 200, a: 255 },
  brushSize: 1,
  shadeStrength: 10,
  strokeSeed: 1,
  modelType: 'classic',
  visibleParts: initialVisibility,
  mirrorMode: false,
  isLoading: false,
  viewerMode: 'paint',
  showGrid: true,
  shapePreview: null,
  hoverCoords: null,
  layerViewMode: 'both',
  dirty: false,
  explicitLoad: false,
  restored: false,
};

export const SHADE_STRENGTH_MIN = 1;
export const SHADE_STRENGTH_MAX = 50;

export type LayerOp =
  | { op: 'add'; name?: string; pixels?: Uint8ClampedArray }
  | { op: 'delete'; id: string }
  | { op: 'duplicate'; id: string }
  | { op: 'move'; id: string; dir: 1 | -1 }
  | { op: 'mergeDown'; id: string }
  | { op: 'flatten' };

export type EditorAction =
  | { type: 'SET_TOOL'; tool: Tool }
  | { type: 'SET_COLOR'; color: Color }
  | { type: 'SET_SECONDARY_COLOR'; color: Color }
  | { type: 'SET_BRUSH_SIZE'; size: BrushSize }
  | { type: 'SET_SHADE_STRENGTH'; strength: number }
  | { type: 'SET_MODEL_TYPE'; modelType: ModelType }
  | { type: 'TOGGLE_PART_VISIBILITY'; part: BodyPart; layer: Layer }
  | { type: 'SET_PART_VISIBILITY'; part: BodyPart; visible: boolean }
  | { type: 'SET_ALL_VISIBILITY'; visible: boolean }
  | { type: 'SET_ALL_OVERLAY_VISIBILITY'; visible: boolean }
  | { type: 'TOGGLE_MIRROR' }
  | { type: 'SET_VIEWER_MODE'; mode: 'rotate' | 'paint' }
  | { type: 'TOGGLE_GRID' }
  | { type: 'SET_LAYER_VIEW_MODE'; mode: LayerViewMode }
  | { type: 'SET_SHAPE_PREVIEW'; preview: ShapePreview | null }
  | { type: 'SET_HOVER_COORDS'; coords: { x: number; y: number } | null }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'BEGIN_STROKE' }
  | { type: 'STROKE'; points: Point[] }
  | { type: 'END_STROKE' }
  /** Take the open stroke back entirely: no pixels, no undo entry (a touch that became a pinch). */
  | { type: 'CANCEL_STROKE' }
  | { type: 'SHAPE'; tool: Tool; x0: number; y0: number; x1: number; y1: number; clip?: Bounds }
  | { type: 'FILL'; x: number; y: number }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'CLEAR_ALL' }
  /** "Start new" after a restored session: a blank document AND the default (classic) model, undoable. */
  | { type: 'START_NEW' }
  | { type: 'LOAD_SKIN'; pixels: Uint8ClampedArray; name?: string; source: 'user' | 'handoff'; modelType?: ModelType }
  | { type: 'MARK_EXPLICIT_LOAD' }
  /** The explicit load announced by MARK_EXPLICIT_LOAD failed: the autosave restore may run again. */
  | { type: 'EXPLICIT_LOAD_FAILED' }
  | { type: 'RESTORE'; saved: AutosaveState }
  | { type: 'DISMISS_RESTORED' }
  | { type: 'LAYER_OP'; payload: LayerOp }
  | { type: 'SET_LAYER_PROPS'; id: string; props: Partial<Pick<SkinLayer, 'name' | 'visible' | 'opacity' | 'locked'>>; coalesceKey?: string }
  | { type: 'SELECT_LAYER'; id: string };

function settingsOf(state: EditorState): PaintSettings {
  return {
    color: state.color, brushSize: state.brushSize, model: state.modelType, mirror: state.mirrorMode,
    strength: state.shadeStrength / 100, seed: state.strokeSeed,
  };
}

const snapshotOf = (state: EditorState): EditorSnapshot => ({ doc: state.doc, modelType: state.modelType });
const docOfSnapshot = (s: EditorSnapshot) => s.doc;
function record(state: EditorState, previous: EditorSnapshot = snapshotOf(state)): History<EditorSnapshot> {
  return recordHistory(state.history, previous, {}, docOfSnapshot);
}

/**
 * Commit a one-shot edit as its own undo step. No-op (same state) when neither the document nor the
 * model changed.
 */
function commit(state: EditorState, next: SkinDocument, coalesceKey: string | null = null, nextModel: ModelType = state.modelType): EditorState {
  if (next === state.doc && nextModel === state.modelType) return state;
  const coalesce = coalesceKey !== null && coalesceKey === state.lastCoalesceKey;
  return {
    ...state,
    doc: next,
    modelType: nextModel,
    history: coalesce ? state.history : record(state),
    strokeBase: null,
    strokeRecorded: false,
    lastCoalesceKey: coalesceKey,
    dirty: true,
  };
}

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'SET_TOOL':
      return { ...state, tool: action.tool, shapePreview: null };
    case 'SET_COLOR':
      return { ...state, color: action.color };
    case 'SET_SECONDARY_COLOR':
      return { ...state, secondaryColor: action.color };
    case 'SET_BRUSH_SIZE':
      return { ...state, brushSize: action.size };
    case 'SET_SHADE_STRENGTH': {
      if (!Number.isFinite(action.strength)) return state;
      const v = Math.round(Math.min(SHADE_STRENGTH_MAX, Math.max(SHADE_STRENGTH_MIN, action.strength)));
      return v === state.shadeStrength ? state : { ...state, shadeStrength: v };
    }
    case 'SET_MODEL_TYPE':
      // Its own undo step: the model is part of every snapshot, so changing it must be one to undo.
      return commit(state, state.doc, null, action.modelType);
    case 'TOGGLE_PART_VISIBILITY': {
      const cur = state.visibleParts[action.part];
      return { ...state, visibleParts: { ...state.visibleParts, [action.part]: { ...cur, [action.layer]: !cur[action.layer] } } };
    }
    case 'SET_PART_VISIBILITY':
      return { ...state, visibleParts: { ...state.visibleParts, [action.part]: { base: action.visible, overlay: action.visible } } };
    case 'SET_ALL_VISIBILITY':
      return { ...state, visibleParts: allVisibility(action.visible, action.visible) };
    case 'SET_ALL_OVERLAY_VISIBILITY': {
      const v = state.visibleParts;
      const next = { ...v };
      (Object.keys(v) as BodyPart[]).forEach(p => { next[p] = { ...v[p], overlay: action.visible }; });
      return { ...state, visibleParts: next };
    }
    case 'TOGGLE_MIRROR':
      return { ...state, mirrorMode: !state.mirrorMode };
    case 'SET_VIEWER_MODE':
      return { ...state, viewerMode: action.mode };
    case 'TOGGLE_GRID':
      return { ...state, showGrid: !state.showGrid };
    case 'SET_LAYER_VIEW_MODE':
      return { ...state, layerViewMode: action.mode };
    case 'SET_SHAPE_PREVIEW':
      return { ...state, shapePreview: action.preview };
    case 'SET_HOVER_COORDS': {
      const a = state.hoverCoords, b = action.coords;
      if (a === b || (a && b && a.x === b.x && a.y === b.y)) return state;
      return { ...state, hoverCoords: b };
    }
    case 'SET_LOADING':
      return { ...state, isLoading: action.loading };

    // ---- strokes: one undo entry per stroke, recorded on the first pixel that actually changes ----
    case 'BEGIN_STROKE':
      // ALWAYS a fresh stroke. A stroke whose END_STROKE was lost (pointer capture lost, a 3D paint-end
      // missed) is already committed — its undo entry was recorded on its first change — so starting a
      // new one just closes it. (It used to return early, merging the next stroke into the old one: two
      // strokes, one undo step, and the shading brushes read a stale starting layer.)
      // A new seed per stroke: the Noise brush is stable WITHIN a stroke and fresh for the next one.
      return {
        ...state, strokeBase: state.doc, strokeRecorded: false, strokeHistory: state.history,
        strokeSeed: (state.strokeSeed % 0x7fffffff) + 1,
      };
    case 'STROKE': {
      // No open stroke (never begun, or ended by an UNDO/REDO pressed mid-drag): ignore the stray
      // samples. The rest of that drag paints nothing, instead of recording one undo step per move.
      if (!state.strokeBase) return state;
      const next = paintStroke(state.doc, state.tool, action.points, settingsOf(state), state.strokeBase);
      if (next === state.doc) return state;
      return {
        ...state,
        doc: next,
        history: state.strokeRecorded ? state.history : record(state, { doc: state.strokeBase, modelType: state.modelType }),
        strokeRecorded: true,
        lastCoalesceKey: null,
        dirty: true,
      };
    }
    case 'END_STROKE':
      if (!state.strokeBase && !state.strokeRecorded) return state;
      return { ...state, strokeBase: null, strokeRecorded: false, strokeHistory: null };
    case 'CANCEL_STROKE': {
      // Only an OPEN stroke (strokeBase is cleared by END_STROKE, UNDO/REDO, loads and every commit, so
      // a stale strokeHistory can never be put back over later edits).
      if (!state.strokeBase) return state;
      if (!state.strokeRecorded) return { ...state, strokeBase: null, strokeHistory: null };
      return {
        ...state,
        doc: state.strokeBase,
        history: state.strokeHistory ?? state.history,
        strokeBase: null, strokeRecorded: false, strokeHistory: null,
      };
    }

    case 'SHAPE':
      return {
        ...commit(state, paintShape(state.doc, action.tool, action.x0, action.y0, action.x1, action.y1, settingsOf(state), action.clip)),
        shapePreview: null,
      };
    case 'FILL':
      return commit(state, paintFill(state.doc, action.x, action.y, settingsOf(state)));

    case 'UNDO': {
      const r = undoHistory(state.history, snapshotOf(state));
      if (!r) return state;
      return {
        ...state, doc: r.present.doc, modelType: r.present.modelType, history: r.history,
        strokeBase: null, strokeRecorded: false, lastCoalesceKey: null, dirty: true,
      };
    }
    case 'REDO': {
      const r = redoHistory(state.history, snapshotOf(state));
      if (!r) return state;
      return {
        ...state, doc: r.present.doc, modelType: r.present.modelType, history: r.history,
        strokeBase: null, strokeRecorded: false, lastCoalesceKey: null, dirty: true,
      };
    }

    case 'CLEAR_ALL':
      return commit(state, isBlankDocument(state.doc) && state.doc.layers.length === 1 ? state.doc : createBlankDocument());
    case 'START_NEW': {
      const blank = isBlankDocument(state.doc) && state.doc.layers.length === 1 ? state.doc : createBlankDocument();
      return { ...commit(state, blank, null, initialState.modelType), restored: false };
    }

    // ---- loading / autosave ----
    case 'MARK_EXPLICIT_LOAD':
      return { ...state, explicitLoad: true, restored: false };
    case 'EXPLICIT_LOAD_FAILED':
      // RESTORE still refuses once the user has edited (dirty), so nothing they did is replaced.
      return state.explicitLoad ? { ...state, explicitLoad: false } : state;
    case 'LOAD_SKIN': {
      if (action.pixels.length !== SKIN_BYTES) return state;
      const doc = documentFromImportedSkin(action.pixels, action.name);
      if (action.source === 'handoff') {
        // Opened WITH a skin (?skinUrl= or the Saved page): a fresh session. Not marked dirty, so an
        // earlier autosaved session is only replaced once the user actually edits this one.
        return {
          ...state, doc, history: emptyHistory(), strokeBase: null, strokeRecorded: false, lastCoalesceKey: null,
          modelType: action.modelType ?? state.modelType, explicitLoad: true, restored: false,
        };
      }
      // Loaded from inside the editor: undoable, like any other edit — the model with it.
      return { ...commit(state, doc, null, action.modelType ?? state.modelType), explicitLoad: true, restored: false };
    }
    case 'RESTORE':
      // An explicit load always wins, and never clobber anything the user has already done.
      if (state.explicitLoad || state.dirty) return state;
      return {
        ...state,
        doc: action.saved.doc,
        modelType: action.saved.modelType,
        layerViewMode: action.saved.layerViewMode,
        history: emptyHistory(),
        restored: true,
      };
    case 'DISMISS_RESTORED':
      return state.restored ? { ...state, restored: false } : state;

    // ---- layers ----
    case 'LAYER_OP': {
      const p = action.payload;
      const doc = state.doc;
      const next =
        p.op === 'add' ? addLayerOp(doc, { name: p.name, pixels: p.pixels })
        : p.op === 'delete' ? deleteLayerOp(doc, p.id)
        : p.op === 'duplicate' ? duplicateLayerOp(doc, p.id)
        : p.op === 'move' ? moveLayerOp(doc, p.id, p.dir)
        : p.op === 'mergeDown' ? mergeDownOp(doc, p.id)
        : flattenDocument(doc);
      return commit(state, next);
    }
    case 'SET_LAYER_PROPS':
      return commit(state, updateLayerProps(state.doc, action.id, action.props), action.coalesceKey ?? null);
    case 'SELECT_LAYER': {
      const next = setActiveLayerOp(state.doc, action.id);
      // Selecting is not an undo step, but it IS part of the saved document.
      return next === state.doc ? state : { ...state, doc: next, dirty: true };
    }

    default:
      return state;
  }
}
